import { useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  Eye,
  Archive as ArchiveIcon,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useApproveContent,
  useArchiveContent,
  useRejectContent,
  useUnpublishContent,
} from '@/hooks/use-content';
import { toToastMessage } from '@/lib/api-error';
import { ContentStatus, type ContentItem } from '@/types/content';
import { RejectReasonDialog } from './reject-reason-dialog';

// SPEC-021: Admin-only action bar shown above the editor form. The set of
// available actions is driven by the item's current status — e.g. only
// pending_review items can be approved, only published items can be
// unpublished or archived.

interface ModerationActionsBarProps {
  item: ContentItem;
}

export function ModerationActionsBar({ item }: ModerationActionsBarProps) {
  const approve = useApproveContent();
  const reject = useRejectContent();
  const unpublish = useUnpublishContent();
  const archive = useArchiveContent();

  const [rejectOpen, setRejectOpen] = useState(false);

  const pending = approve.isPending || reject.isPending || unpublish.isPending || archive.isPending;

  const canApprove = item.status === ContentStatus.PendingReview;
  const canReject = item.status === ContentStatus.PendingReview;
  const canUnpublish = item.status === ContentStatus.Published;
  const canArchive = item.status === ContentStatus.Published;

  const noActions = !canApprove && !canReject && !canUnpublish && !canArchive;

  const handleApprove = () => {
    approve.mutate(item.id, {
      onSuccess: () => toast.success('Approved and published'),
      onError: (e) => toast.error(toToastMessage(e, 'Approve failed')),
    });
  };
  const handleUnpublish = () => {
    unpublish.mutate(item.id, {
      onSuccess: () => toast.success('Unpublished — now draft'),
      onError: (e) => toast.error(toToastMessage(e, 'Unpublish failed')),
    });
  };
  const handleArchive = () => {
    if (!window.confirm('Archive this content? It will be hidden from the student feed.')) return;
    archive.mutate(item.id, {
      onSuccess: () => toast.success('Archived'),
      onError: (e) => toast.error(toToastMessage(e, 'Archive failed')),
    });
  };
  const handleReject = async (reason: string) => {
    try {
      await reject.mutateAsync({ id: item.id, reason });
      toast.success('Rejected');
      setRejectOpen(false);
    } catch (e) {
      toast.error(toToastMessage(e, 'Reject failed'));
    }
  };

  return (
    <>
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex flex-wrap items-center gap-3 pt-4">
          <p className="text-sm font-medium">Admin moderation</p>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canApprove && (
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={pending}
              >
                {approve.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                )}
                Approve
              </Button>
            )}
            {canReject && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setRejectOpen(true)}
                disabled={pending}
              >
                <XCircle className="mr-1 h-4 w-4" />
                Reject
              </Button>
            )}
            {canUnpublish && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleUnpublish}
                disabled={pending}
              >
                {unpublish.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="mr-1 h-4 w-4" />
                )}
                Unpublish
              </Button>
            )}
            {canArchive && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleArchive}
                disabled={pending}
              >
                {archive.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <ArchiveIcon className="mr-1 h-4 w-4" />
                )}
                Archive
              </Button>
            )}
            {noActions && (
              <span className="text-xs text-muted-foreground">
                No moderation actions available for status &quot;{item.status}&quot;.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <RejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        busy={reject.isPending}
        onSubmit={handleReject}
      />
    </>
  );
}
